import React, { useEffect, useState } from "react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n";
import { api } from "../api";

const Kurumsal = () => {
  const { t } = useLang();
  const [k, setK] = useState(null);

  useEffect(() => {
    api.kurumsal().then(setK).catch(() => {});
  }, []);

  if (!k) {
    return (
      <main className="bg-white">
        <SEO title={t("kurumsal.title")} description={t("kurumsal.subtitle")} path="/kurumsal" />
        <PageHero kicker={t("kurumsal.kicker")} title={t("kurumsal.title")} subtitle={t("kurumsal.subtitle")} />
      </main>
    );
  }

  return (
    <main className="bg-white">
      <SEO title={t("kurumsal.title")} description={t("kurumsal.subtitle")} path="/kurumsal" />
      <PageHero kicker={t("kurumsal.kicker")} title={t("kurumsal.title")} subtitle={t("kurumsal.subtitle")} />

      <section className="py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-neutral-100">
            <img
              src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODk0ODM1MDN8MA&ixlib=rb-4.1.0&q=85&w=1200"
              alt="Koptur Grup A.Ş."
              className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
            />
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#1a1a1a] leading-tight">{k.story_title}</h2>
            <div className="mt-5 md:mt-6 space-y-4 text-neutral-600 text-base md:text-[17px] leading-relaxed whitespace-pre-line">
              <p>{k.story_p1}</p>
              <p>{k.story_p2}</p>
            </div>
            <div className="mt-6 md:mt-8 grid grid-cols-3 gap-4 md:gap-6">
              <div>
                <div className="text-2xl md:text-3xl font-black text-[#4E0D1C]">{k.stat_years}</div>
                <div className="text-xs md:text-sm text-neutral-500 mt-1">{t("kurumsal.years")}</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-black text-[#4E0D1C]">{k.stat_projects}</div>
                <div className="text-xs md:text-sm text-neutral-500 mt-1">{t("kurumsal.projects")}</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-black text-[#4E0D1C]">{k.stat_areas}</div>
                <div className="text-xs md:text-sm text-neutral-500 mt-1">{t("kurumsal.areas")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-6 md:gap-8">
          {[
            { title: t("kurumsal.mission"), text: k.mission_text },
            { title: t("kurumsal.vision"), text: k.vision_text },
          ].map((b) => (
            <div key={b.title} className="bg-white rounded-2xl p-6 md:p-10 border border-neutral-100 hover:shadow-[0_20px_40px_rgba(0,0,0,0.06)] transition-shadow duration-300">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-[2px] w-8 bg-[#C9A24B]" />
                <span className="text-[#C9A24B] text-xs font-semibold tracking-[0.24em] uppercase">{t("kurumsal.kicker")}</span>
              </div>
              <h3 className="text-xl md:text-3xl font-black text-[#1a1a1a]">{b.title}</h3>
              <p className="mt-4 text-neutral-600 leading-relaxed text-[15px] md:text-base whitespace-pre-line">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {k.values?.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-4xl font-black text-[#1a1a1a]">{t("kurumsal.values")}</h2>
            <div className="mt-8 md:mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {k.values.map((v, i) => (
                <div key={i} className="group rounded-2xl border border-neutral-100 p-5 md:p-6 hover:border-[#C9A24B] transition-colors duration-300 bg-white">
                  <CheckCircle2 className="w-7 h-7 md:w-8 md:h-8 text-[#C9A24B]" />
                  <div className="mt-3 md:mt-4 text-base md:text-lg font-bold text-[#1a1a1a]">{v.title}</div>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {k.milestones?.length > 0 && (
        <section className="py-12 md:py-16 bg-[#4E0D1C] text-white">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-4xl font-black text-white">{t("kurumsal.timeline")}</h2>
            <div className="mt-8 md:mt-10 relative">
              <div className="absolute left-0 right-0 top-6 h-[2px] bg-white/15 hidden md:block" />
              <div className="grid grid-cols-2 md:grid-cols-6 gap-6 relative">
                {k.milestones.map((m, i) => (
                  <div key={i} className="relative">
                    <div className="w-3 h-3 rounded-full bg-[#C9A24B] mx-auto md:mx-0 shadow-[0_0_0_6px_rgba(201,162,75,0.18)]" />
                    <div className="mt-5 font-black text-xl md:text-2xl text-[#C9A24B]">{m.year}</div>
                    <p className="mt-2 text-sm text-white/80 leading-relaxed">{m.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default Kurumsal;
